/**
 * ChampionshipPdfExport.jsx
 * Botão que exporta TODOS os gráficos da aba Stats como PDF multi-página.
 *
 * Fluxo:
 *  1. Usuário clica em "PDF"
 *  2. onBeforePrint() → ativa printAll no pai (todos os painéis renderizados)
 *  3. Aguarda 1400ms para recharts terminar de renderizar
 *  4. window.print() → diálogo de impressão/salvar PDF
 *  5. afterprint event → onAfterPrint() desativa printAll (volta à UI normal)
 *
 * CSS @media print:
 *  - Oculta sidebar, header, nav, botões
 *  - Exibe header de impressão com logo VUKZ + CBDA + nome
 *  - Cada seção (.vukz-print-section) começa em nova página
 *  - Cards de gráfico não são partidos no meio
 */
import React, { useEffect } from 'react';
import { FileDown, Loader } from 'lucide-react';

const BRAND_GREEN = '#064e3b';

const PRINT_STYLE_ID = 'vukz-championship-print-styles';

function injectPrintStyles() {
  if (document.getElementById(PRINT_STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = PRINT_STYLE_ID;
  style.innerHTML = `
    #championship-print-header { display: none; }

    @media print {
      nav, aside,
      .championship-tabs-nav,
      [class*="GlobalSidebar"],
      [class*="SocialHeader"],
      [class*="MobileMenu"],
      .no-print,
      [data-no-print] {
        display: none !important;
      }

      body, html { margin: 0 !important; padding: 0 !important; background: #fff !important; }
      #root > * { padding-top: 0 !important; margin-left: 0 !important; }
      * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }

      #championship-print-header {
        display: flex !important;
        align-items: center;
        gap: 14px;
        padding: 10px 16px 14px;
        border-bottom: 3px solid ${BRAND_GREEN};
        margin-bottom: 0;
        page-break-after: avoid;
        break-after: avoid;
      }
      #championship-print-header img { height: 44px; width: auto; object-fit: contain; }
      #championship-print-header img.logo-right { opacity: 0.5; height: 38px; }
      #championship-print-header .ph-title {
        flex: 1;
        font-size: 17px;
        font-weight: 800;
        color: ${BRAND_GREEN};
        line-height: 1.2;
      }
      #championship-print-header .ph-sub {
        font-size: 11px;
        color: #6b7280;
        margin-top: 3px;
      }

      /* Cada seção = nova página */
      .vukz-print-section {
        page-break-before: always !important;
        break-before: page !important;
        padding: 8px 12px 12px;
      }
      .vukz-print-section:first-child {
        page-break-before: avoid !important;
        break-before: avoid !important;
      }

      /* Cards não quebram no meio */
      .bg-white.rounded-xl,
      .recharts-wrapper {
        page-break-inside: avoid !important;
        break-inside: avoid !important;
        margin-bottom: 10px !important;
      }

      /* Grid 2 cols → 1 col na impressão */
      .grid.grid-cols-1.lg\\:grid-cols-2 {
        grid-template-columns: 1fr !important;
      }

      .bg-gradient-to-r {
        background: ${BRAND_GREEN} !important;
      }
    }
  `;
  document.head.appendChild(style);
}

export default function ChampionshipPdfExport({ competitionName, year, printing, onBeforePrint, onAfterPrint }) {
  useEffect(() => { injectPrintStyles(); }, []);

  useEffect(() => {
    const handler = () => { if (onAfterPrint) onAfterPrint(); };
    window.addEventListener('afterprint', handler);
    return () => window.removeEventListener('afterprint', handler);
  }, [onAfterPrint]);

  const handleClick = () => {
    if (printing) return;
    if (onBeforePrint) onBeforePrint();
    // Aguarda recharts renderizar todos os gráficos
    setTimeout(() => { window.print(); }, 1400);
  };

  return (
    <>
      {/* Cabeçalho visível APENAS na impressão */}
      <div id="championship-print-header">
        <img src="/vukz-logo.png" alt="VUKZ" onError={e => { e.target.style.display = 'none'; }} />
        <img src="/logo campeonatos natacao/cbda.png" alt="CBDA" onError={e => { e.target.style.display = 'none'; }} />
        <div>
          <div className="ph-title">{competitionName || 'Campeonato de Natação'}</div>
          <div className="ph-sub">Relatório Completo de Estatísticas · {year} · VUKZ Sports Intelligence</div>
        </div>
        <img className="logo-right" src="/vukz-logo.png" alt="" onError={e => { e.target.style.display = 'none'; }} />
      </div>

      {/* Botão em tela */}
      <button
        onClick={handleClick}
        disabled={printing}
        data-no-print
        className="no-print flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 disabled:opacity-60 text-sm font-medium text-gray-600 hover:text-gray-900 transition-all"
        title="Exportar todos os gráficos como PDF"
      >
        {printing
          ? <><Loader size={15} className="animate-spin" /><span className="hidden sm:inline">Preparando…</span></>
          : <><FileDown size={15} /><span className="hidden sm:inline">PDF</span></>
        }
      </button>
    </>
  );
}
